import { APP_URL, BACKEND_URL } from 'src/constants';
import { makeAsyncCall, createFilterParamsString } from 'src/utils';
import { IUser } from 'src/types';

const handleGetUser = (spaceDomain?: string) =>
  makeAsyncCall({
    method: 'GET',
    url: `${BACKEND_URL}oauth/user${spaceDomain ? `?space_domain=${spaceDomain}` : ''}`,
  });

const handleGetUserToken = (
  values: {
    username: string;
    password: string;
    grant_type: string;
  },
  survey_id?: number,
  space_id?: number
) =>
  makeAsyncCall({
    method: 'POST',
    url: `${BACKEND_URL}oauth/token?${
      survey_id ? `survey-context=${survey_id}` : `space-context=${space_id}`
    }&redirect_overwrite_url=${encodeURI(APP_URL)}`,
    body: JSON.stringify(values),
  });

const handleGetUserTokenFromSignedUrl = (
  userId: number,
  expires: number,
  signature: string,
  spaceUser: boolean
) =>
  makeAsyncCall({
    method: 'GET',
    url: `${BACKEND_URL}oauth/${
      spaceUser ? 'space-user' : 'user'
    }/${userId}/activate?expires=${expires}&signature=${signature}`,
  });

const handleResetPasswordRequest = (values: {
  email: string;
  survey_id?: number;
  custom_domain?: string;
}) =>
  makeAsyncCall({
    url: `${BACKEND_URL}oauth/password/email`,
    method: 'POST',
    body: JSON.stringify(values),
  });

const handleResetPasswordSet = (values: {
  email: string;
  token: string;
  password: string;
  password_confirmation: string;
}) =>
  makeAsyncCall({
    url: `${BACKEND_URL}oauth/password/set`,
    method: 'POST',
    body: JSON.stringify(values),
  });

const handleCreateAccount = values =>
  makeAsyncCall({
    url: `${BACKEND_URL}participants/register`,
    method: 'POST',
    body: JSON.stringify(values),
  });

const handleUpdateUser = user =>
  makeAsyncCall({
    url: `${BACKEND_URL}users/${user.id}`,
    method: 'PUT',
    body: JSON.stringify(user),
  });

const handleGetSurveyUsers = surveyId =>
  makeAsyncCall({
    url: `${BACKEND_URL}surveys/${surveyId}/users`,
    method: 'GET',
  });

const handleActivationToken = token =>
  makeAsyncCall({
    url: `${BACKEND_URL}oauth/onboarding-token`,
    method: 'POST',
    body: JSON.stringify(token),
  });

const handleLogout = () => {
  return makeAsyncCall({
    url: `${BACKEND_URL}oauth/logout`,
    method: 'POST',
  }).then(res => {
    document.cookie = '';
    return res;
  });
};

const unlock = userId =>
  makeAsyncCall({
    url: `${BACKEND_URL}users/${userId}/unlock`,
    method: 'POST',
  });

const index = query =>
  makeAsyncCall({
    url: createFilterParamsString(query, `${BACKEND_URL}users`),
    method: 'GET',
  });

const getCategories = () =>
  makeAsyncCall({
    url: `${BACKEND_URL}categories`,
    method: 'GET',
  });

const checkPswResetLink = (email, token) =>
  makeAsyncCall({
    url: `${BACKEND_URL}oauth/password/reset-left-time?email=${email}&token=${token}`,
    method: 'GET',
  });

const handleValidateUserCredentials = vals =>
  makeAsyncCall({
    url: `${BACKEND_URL}participants/validate-registration-first-step`,
    method: 'POST',
    body: JSON.stringify(vals),
  });

const handleValidateUserCredentialsPublicState = vals =>
  makeAsyncCall({
    url: `${BACKEND_URL}participants/validate-first-logon-public-stage-first-step`,
    method: 'POST',
    body: JSON.stringify(vals),
  });

const handleUnionConflictedSpaceUsers = vals =>
  makeAsyncCall({
    url: `${BACKEND_URL}space-users/union-conflicted-space-users-and-login`,
    method: 'POST',
    body: JSON.stringify(vals),
  });

const handleCheckEmailVerifiedLink = (userId, expires, signature, token, surveyId) => {
  const surveyQueryParam = surveyId ? `&survey_id=${surveyId}` : '';

  return makeAsyncCall({
    url: `${BACKEND_URL}oauth/user/${userId}/verify-email?expires=${expires}&signature=${signature}&token=${token}${surveyQueryParam}`,
    method: 'PUT',
  });
};

const handleResendEmail = data =>
  makeAsyncCall({
    url: `${BACKEND_URL}oauth/send-email-verification-link`,
    method: 'PUT',
    body: JSON.stringify(data),
  });

const handleVerifyEmail = (userId?: number) =>
  makeAsyncCall({
    url: `${BACKEND_URL}users/${userId}/verify-email`,
    method: 'PUT',
  });

const handleVerifyPhone = data =>
  makeAsyncCall({
    url: `${BACKEND_URL}oauth/send-two-fa-token`,
    method: 'POST',
    body: JSON.stringify(data),
  });

const handleVerifyToken = data =>
  makeAsyncCall({
    url: `${BACKEND_URL}oauth/second-factor-login`,
    method: 'POST',
    body: JSON.stringify(data),
  });

const handleSetup2FA = data =>
  makeAsyncCall({
    url: `${BACKEND_URL}oauth/setup-2fa`,
    method: 'PUT',
    body: JSON.stringify(data),
  });

const handleVerifyPhone2FA = data =>
  makeAsyncCall({
    url: `${BACKEND_URL}oauth/verify-phone`,
    method: 'PUT',
    body: JSON.stringify(data),
  });

const handleSingleSignOnRedirect = (data, provider) => {
  let url = '';
  switch (provider) {
    case 'microsoft-login':
      url = `${BACKEND_URL}sso/azure/redirect-url?callback_url=${btoa(data)}&redirect_overwrite_url=${encodeURI(APP_URL)}`;
      break;
    case 'bs':
      url = `${BACKEND_URL}sso/bs/redirect-url?callback_url=${btoa(data)}`;
      break;
    case 'ezug':
      url = `${BACKEND_URL}sso/e-zug/redirect-url?callback_url=${btoa(data)}`;
  }
  return makeAsyncCall({
    url,
    method: 'GET',
  });
};
const handleSingleSignOnLogin = (data: any, survey_id?: number) =>
  makeAsyncCall({
    url: `${BACKEND_URL}oauth/token/sso-login${
      survey_id ? `?survey-context=${survey_id}` : ''
    }${survey_id ? `&redirect_overwrite_url=${encodeURI(APP_URL)}` : `?redirect_overwrite_url=${encodeURI(APP_URL)}`}`,
    method: 'POST',
    body: JSON.stringify(data),
  });

const handleSingleSignOns = data =>
  makeAsyncCall({
    url: `${BACKEND_URL}single-sign-ons?space_user_id=${data}`,
    method: 'GET',
  });

const handleRemoveSingleSignOnProvider = (id, data) =>
  makeAsyncCall({
    url: `${BACKEND_URL}single-sign-ons/${id}`,
    method: 'DELETE',
    body: JSON.stringify(data),
  });

export const UserService = {
  getUserToken: (
    values: { username: string; password: string; grant_type: string },
    survey_id?: number,
    space_id?: number
  ) => handleGetUserToken(values, survey_id, space_id),
  getUser: (spaceDomain?: string) => handleGetUser(spaceDomain),
  getUserTokenFromSignedUrl: (
    userId: number,
    expires: number,
    signature: string,
    spaceUser: boolean
  ) => handleGetUserTokenFromSignedUrl(userId, expires, signature, spaceUser),
  resetPasswordRequest: (values: {
    email: string;
    survey_id?: number;
    custom_domain?: string;
  }) => handleResetPasswordRequest(values),
  resetPasswordSet: (values: {
    email: string;
    token: string;
    password: string;
    password_confirmation: string;
  }) => handleResetPasswordSet(values),
  createAccount: values => handleCreateAccount(values),
  updateUser: (user: IUser) => handleUpdateUser(user),
  getSurveyUsers: surveyId => handleGetSurveyUsers(surveyId),
  logout: () => handleLogout(),
  activationToken: token => handleActivationToken(token),
  unlock: userId => unlock(userId),
  index: query => index(query),
  categories: () => getCategories(),
  validateUserCredentials: vals => handleValidateUserCredentials(vals),
  validateUserCredentialsPublicState: vals =>
    handleValidateUserCredentialsPublicState(vals),
  checkPswResetLink: (email, token) => checkPswResetLink(email, token),
  updateExistedUser: vals => Promise.resolve({ statusCode: 200, body: vals }),
  unionConflictedSpaceUsers: vals => handleUnionConflictedSpaceUsers(vals),
  checkEmailVerifiedLink: (userId, expires, signature, token, surveyId) =>
    handleCheckEmailVerifiedLink(userId, expires, signature, token, surveyId),
  resendEmail: (data: { survey_id?: number; space_id?: number }) =>
    handleResendEmail(data),
  verifyEmail: (userId?: number) => handleVerifyEmail(userId),
  verifyPhone: (data: { resend?: boolean }) => handleVerifyPhone(data),
  verifyToken: (data: { token: string }) => handleVerifyToken(data),
  verifySetup2FA: (data: { phone: string }) => handleSetup2FA(data),
  verifyPhone2FA: (data: { token: string }) => handleVerifyPhone2FA(data),
  singleSignOnRedirect: (data: string, provider: string) =>
    handleSingleSignOnRedirect(data, provider),
  singleSignOnLogin: (data: any, survey_id?: number | undefined) =>
    handleSingleSignOnLogin(data, survey_id),
  singleSignOns: (data: number | undefined) => handleSingleSignOns(data),
  removeSingleSignOnProvider: (id: number, data: { space_user_id: number | undefined }) =>
    handleRemoveSingleSignOnProvider(id, data),
};